import type { TaskReminder, Todo } from "../../domain/entities.ts";
import { resolveWallTime, shiftWallTime, wallTimeInZone } from "./task-time.ts";

export const REMINDER_OFFSETS = [15, 60, 1440] as const;
const LEGACY_REMINDER_ID = "00000000-0000-4000-8000-000000000001";

export function normalizeReminders(todo: Pick<Todo, "reminders" | "reminderAt">): TaskReminder[] {
  if (Array.isArray(todo.reminders) && todo.reminders.length) return todo.reminders.filter(validReminder).slice(0, 10).map(item => ({ id: item.id, mode: item.mode, at: item.mode === "absolute" ? item.at! : null, offsetMinutes: item.mode === "relative" ? item.offsetMinutes! : null, recipients: [...new Set((item.recipients || []).filter(id => typeof id === "string"))] }));
  return todo.reminderAt ? [{ id: LEGACY_REMINDER_ID, mode: "absolute", at: todo.reminderAt, offsetMinutes: null, recipients: [] }] : [];
}

export function validReminder(item: unknown): item is TaskReminder {
  if (!item || typeof item !== "object") return false;
  const reminder = item as TaskReminder;
  if (typeof reminder.id !== "string" || !reminder.id || !["absolute", "relative"].includes(reminder.mode)) return false;
  if (reminder.mode === "absolute") return typeof reminder.at === "string" && !Number.isNaN(new Date(reminder.at).getTime());
  return Number.isInteger(reminder.offsetMinutes) && reminder.offsetMinutes! >= 0 && reminder.offsetMinutes! <= 525_600;
}

export function reminderLabel(reminder: TaskReminder): string {
  if (reminder.mode === "absolute") return reminder.at || "Exact time";
  const minutes = reminder.offsetMinutes || 0;
  if (minutes === 0) return "At task time";
  if (minutes % 1440 === 0) return `${minutes / 1440} ${minutes === 1440 ? "day" : "days"} before`;
  if (minutes % 60 === 0) return `${minutes / 60} ${minutes === 60 ? "hour" : "hours"} before`;
  return `${minutes} minutes before`;
}

export function shiftAbsoluteReminders(reminders: TaskReminder[], dayShift: number, zone?: string | null): TaskReminder[] {
  return reminders.map(reminder => ({ ...reminder, at: reminder.mode === "absolute" && reminder.at ? zone ? resolveWallTime(shiftWallTime(wallTimeInZone(reminder.at, zone), dayShift)!, zone) : new Date(new Date(reminder.at).getTime() + dayShift * 86_400_000).toISOString() : reminder.at, recipients: [...(reminder.recipients || [])] }));
}
